import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { EmptyState, Spinner } from "@/design-system";
import { useAuth } from "@/lib/auth";
import { AppShell } from "./AppShell";
import { Page } from "./Page";
import { LoginPage, RegisterPage } from "@/pages/Auth";

// Kod bo'laklari: har bo'lim alohida yuklanadi (birinchi ochilish tez).
const L = (load, name) => lazy(() => load().then((m) => ({ default: m[name] })));
const pages = {
  Dashboard: L(() => import("@/pages/Dashboard"), "DashboardPage"),
  Pos: L(() => import("@/pages/Pos"), "PosPage"),
  Orders: L(() => import("@/pages/Orders"), "OrdersPage"),
  InvoiceDetail: L(() => import("@/pages/InvoiceDetail"), "InvoiceDetailPage"),
  CustomerDetail: L(() => import("@/pages/CustomerDetail"), "CustomerDetailPage"),
  PayrollDetail: L(() => import("@/pages/PayrollDetail"), "PayrollDetailPage"),
  ProjectDetail: L(() => import("@/pages/ProjectDetail"), "ProjectDetailPage"),
  Money: L(() => import("@/pages/Money"), "MoneyPage"),
  Reports: L(() => import("@/pages/Reports"), "ReportsPage"),
  Equity: L(() => import("@/pages/Equity"), "EquityPage"),
  Budget: L(() => import("@/pages/Budget"), "BudgetPage"),
  Tax: L(() => import("@/pages/Tax"), "TaxPage"),
  Bank: L(() => import("@/pages/Bank"), "BankPage"),
  Edo: L(() => import("@/pages/Edo"), "EdoPage"),
  Settings: L(() => import("@/pages/Settings"), "SettingsPage"),
  Products: L(() => import("@/pages/Products"), "ProductsPage"),
  Customers: L(() => import("@/pages/Simple"), "CustomersPage"),
  Invoices: L(() => import("@/pages/Simple"), "InvoicesPage"),
  Sales: L(() => import("@/pages/Sales"), "SalesPage"),
  Stock: L(() => import("@/pages/Simple"), "StockPage"),
  Bookings: L(() => import("@/pages/Simple"), "BookingsPage"),
  Projects: L(() => import("@/pages/Simple"), "ProjectsPage"),
  Payroll: L(() => import("@/pages/Simple"), "PayrollPage"),
  Okr: L(() => import("@/pages/Simple"), "OkrPage"),
  Audit: L(() => import("@/pages/Simple"), "AuditPage"),
  Team: L(() => import("@/pages/Team"), "TeamPage"),
  Subscriptions: L(() => import("@/pages/Simple"), "SubscriptionsPage"),
};

function Loading() {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "50vh" }}>
      <Spinner />
    </div>
  );
}

function RequireAuth() {
  const { loggedIn, loading, business } = useAuth();
  if (!loggedIn) return <Navigate to="/login" replace />;
  if (loading || !business) return <Loading />;
  return <AppShell />;
}

function Guest() {
  const { loggedIn } = useAuth();
  return loggedIn ? <Navigate to="/" replace /> : <Outlet />;
}

/** Ruxsat bo'lmasa sahifa o'rniga tushuntirish (server baribir 403 qaytaradi). */
function Guard({ permission, children }) {
  const { can } = useAuth();
  if (permission && !can(permission))
    return (
      <Page title="Ruxsat yoʻq">
        <EmptyState title="Bu boʻlim sizning rolingiz uchun yopiq">
          Kerak boʻlsa, biznes egasidan ruxsat soʻrang.
        </EmptyState>
      </Page>
    );
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

const P = (Comp, permission) => (
  <Guard permission={permission}>
    <Comp />
  </Guard>
);

export const routes = [
  {
    element: <Guest />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      { path: "/", element: P(pages.Dashboard, "reports:read") },
      { path: "/pos", element: P(pages.Pos, "sales:write") },
      { path: "/sales", element: P(pages.Sales, "sales:read") },
      { path: "/orders", element: P(pages.Orders, "orders:read") },
      { path: "/bookings", element: P(pages.Bookings, "bookings:read") },
      { path: "/products", element: P(pages.Products, "catalog:read") },
      { path: "/stock", element: P(pages.Stock, "inventory:read") },
      { path: "/customers", element: P(pages.Customers, "crm:read") },
      { path: "/customers/:id", element: P(pages.CustomerDetail, "crm:read") },
      { path: "/invoices", element: P(pages.Invoices, "invoicing:read") },
      { path: "/invoices/:id", element: P(pages.InvoiceDetail, "invoicing:read") },
      { path: "/subscriptions", element: P(pages.Subscriptions, "subscriptions:read") },
      { path: "/money", element: P(pages.Money, "finance:read") },
      { path: "/projects", element: P(pages.Projects, "projects:read") },
      { path: "/projects/:id", element: P(pages.ProjectDetail, "projects:read") },
      { path: "/team", element: P(pages.Team, "hr:read") },
      { path: "/leave", element: <Navigate to="/team?tab=leave" replace /> },
      { path: "/payroll", element: P(pages.Payroll, "payroll:read") },
      { path: "/payroll/:id", element: P(pages.PayrollDetail, "payroll:read") },
      { path: "/okr", element: P(pages.Okr, "okr:read") },
      { path: "/budget", element: P(pages.Budget, "budget:read") },
      { path: "/equity", element: P(pages.Equity, "equity:read") },
      { path: "/edo", element: P(pages.Edo, "edo:read") },
      { path: "/tax", element: P(pages.Tax, "tax:read") },
      { path: "/tax-reports", element: <Navigate to="/tax" replace /> },
      { path: "/bank", element: P(pages.Bank, "banking:read") },
      { path: "/reports", element: P(pages.Reports, "reports:read") },
      { path: "/audit", element: P(pages.Audit, "audit:read") },
      { path: "/settings", element: P(pages.Settings, "members:read") },
      {
        path: "*",
        element: (
          <Page title="Sahifa topilmadi">
            <EmptyState title="Bunday sahifa yoʻq">Manzilni tekshiring yoki menyudan tanlang.</EmptyState>
          </Page>
        ),
      },
    ],
  },
];

export const createRouter = () => createBrowserRouter(routes);
